import {
    createContext, FC, useEffect, useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getPlanVariables, getVariables } from '../../../api/payments';
import { IVariable } from '../../../interfaces/plan';
import { OctopusPaginatorQuery } from '../../../types/paginator';

interface IVariableContext {
    variables: any[],
    loading: boolean,
    total: number,
    paginator: OctopusPaginatorQuery;
    fetchVariables: () => void,
    refreshData: () => void,
    addVariable: (variable: any) => void,
    setPaginator: (paginator: OctopusPaginatorQuery) => void,
    handleSearchParam: (key: string, value: any) => void,
}

const initialContext = {
    variables: [],
    loading: false,
    total: 0,
    paginator: { limit: 10, page: 1 },
    fetchVariables: () => {},
    refreshData: () => {},
    addVariable: (variable: any) => {},
    setPaginator: () => {},
    handleSearchParam: () => {},
};

export const VariableContext = createContext<IVariableContext>(initialContext);

export const VariableProvider: FC = ({ children }) => {
    const [loading, setLoading] = useState<boolean>(initialContext.loading);
    const [variables, setVariables] = useState<IVariable[]>(initialContext.variables);
    const [total, setTotal] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const page = searchParams.get('page') || '1';
    const limit = searchParams.get('limit') || '10';
    const [paginator, setPaginator] = useState<OctopusPaginatorQuery>({ limit: Number(limit), page: Number(page) });

    const { id } = useParams();

    const getParams = () => {
        const params: any = {
            limit: paginator.limit,
            page: paginator.page,
            ...Object.fromEntries(searchParams.entries()),
        };

        if (params.search || params.origin) {
            params.page = 1;
        }

        return params;
    };

    const fetchVariables = async () => {
        if (id) {
            setLoading(true);
            const { data } = await getPlanVariables(id);
            const variables = data.data.sort((a: any, b: any) => {
                if (!a.planID) return 1;
                if (!b.planID) return -1;
                return a.planID - b.planID;
            });
            setVariables(variables);
            setTotal(variables.length);
        } else {
            setLoading(true);

            const { data } = await getVariables(getParams());
            setVariables(data.data.data);
            setTotal(data.data.total);
        }
        setLoading(false);
    };

    const handleSearchParam = (key: string, value: any) => {
        if (value === null || value === undefined || (Array.isArray(value) && value.length === 0) || value === '') {
            searchParams.delete(key);
        } else if (Array.isArray(value)) {
            searchParams.set(key, JSON.stringify(value));
        } else {
            searchParams.set(key, value.toString());
        }

        setSearchParams(searchParams);
    };

    const addVariable = (variable: any) => {
        const exists = variables.find((c: any) => c._id === variable._id);
        if (exists) {
            setVariables((variables: any) => variables.map((c: any) => {
                if (c._id === variable._id) return variable;
                return c;
            }));
        } else {
            setVariables((variables: any) => [...variables, variable]);
        }
    };

    const refreshData = () => fetchVariables();

    useEffect(() => {
        fetchVariables();
    }, [searchParams, paginator.page]);

    return (
        <VariableContext.Provider
            value={{
                variables,
                loading,
                total,
                paginator,
                fetchVariables,
                handleSearchParam,
                addVariable,
                refreshData,
                setPaginator,
            }}
        >
            {children}
        </VariableContext.Provider>
    );
};
